// src/context/LookupContext.tsx

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { LookupValue } from '../services/lookupService';
import { fetchLookupValuesByGroup, fetchCountriesAndCurrencies, fetchStates, fetchCities } from '../services/lookupService';
import { LOOKUP_GROUPS } from '../config/apiConfig';

// --- CONTEXT TYPE DEFINITIONS (No Changes) ---
interface ILookupContext {
  getLabel: (group: string, value: string) => string;
  getOptions: (group: string) => LookupValue[];
  getStates: (countryCode: string) => Promise<LookupValue[]>;
  getCities: (countryCode: string, stateCode: string) => Promise<LookupValue[]>;
  countries: LookupValue[];
  currencies: LookupValue[];
  isLoading: boolean;
}

const LookupContext = createContext<ILookupContext | undefined>(undefined);

// --- THE PROVIDER COMPONENT ---
export const LookupProvider = ({ children }: { children: ReactNode }) => {
  const [lookupMap, setLookupMap] = useState<Map<string, string>>(new Map());
  const [optionsCache, setOptionsCache] = useState<Record<string, LookupValue[]>>({});
  const [countries, setCountries] = useState<LookupValue[]>([]);
  const [currencies, setCurrencies] = useState<LookupValue[]>([]);
  const [locationCache, setLocationCache] = useState<Record<string, LookupValue[]>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAllLookups = async () => {
      setIsLoading(true);
      try {
        // ✅ ADDED ALL LIABILITY-RELATED LOOKUPS TO THE INITIAL GLOBAL FETCH
        const [
          countryData,
          profileTypes,
          relations,
          genders,
          maritalStatuses,
          employmentStatuses,
          ownerTypes,          // New
          liabilityTypes,      // New
          liabilitySubTypes,   // New
          liabilityFrequencies, // New
           assetTypes, 
           assetSubTypes,
           expenseOwnerTypes,
            expenseTypes, 
            expenseSubTypes,
            expenseFrequencies,
            incomeOwnerType,  // New
          incomeType,       // New
          incomeSubType,    // New
          incomeFrequency,
          insuranceType,
          insuranceSubType,
          insuranceInstallmentFrequency,
           goalTypes,       // Add this
  goalSubTypes, 

          
        ] = await Promise.all([
          fetchCountriesAndCurrencies(),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.PROFILE_TYPE),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.RELATION),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.GENDER),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.MARITAL_STATUS),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.EMPLOYMENT_STATUS),
          // --- These will now be fetched only ONCE for the entire app ---
          fetchLookupValuesByGroup(LOOKUP_GROUPS.OWNER_TYPE),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.LIABILITY_TYPE),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.LIABILITY_SUB_TYPE),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.LIABILITY_FREQUENCY),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.ASSET_TYPE),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.ASSET_SUB_TYPE),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.EXPENSE_OWNER_TYPE),
         fetchLookupValuesByGroup(LOOKUP_GROUPS.EXPENSE_TYPE),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.EXPENSE_SUB_TYPE),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.EXPENSE_FREQUENCY),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.INCOME_OWNER_TYPE), // Assuming income uses the same owner type
          fetchLookupValuesByGroup(LOOKUP_GROUPS.INCOME_TYPE),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.INCOME_SUB_TYPE),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.INCOME_FREQUENCY),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.INSURANCE_TYPE),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.INSURANCE_SUB_TYPE),
          fetchLookupValuesByGroup(LOOKUP_GROUPS.INSURANCE_FREQUENCY),
           fetchLookupValuesByGroup(LOOKUP_GROUPS.GOAL_TYPE),      // Add this
          fetchLookupValuesByGroup(LOOKUP_GROUPS.GOAL_SUB_TYPE),
        ]);

        const newLookupMap = new Map<string, string>();
        
        // ✅ ADDED NEW LOOKUPS TO THE GLOBAL CACHE
        const newOptionsCache: Record<string, LookupValue[]> = {
            profileType: profileTypes,
            relation: relations,
            gender: genders,
            maritalStatus: maritalStatuses,
            employmentStatus: employmentStatuses,
            countries: countryData.countries,
            currencies: currencies,
            // --- This data is now cached and available everywhere ---
            ownerType: ownerTypes,
            liabilityType: liabilityTypes,
            liabilitySubType: liabilitySubTypes,
            liabilityFrequency: liabilityFrequencies,
             assetType: assetTypes,
            assetSubType: assetSubTypes,
            expenseOwnerType: expenseOwnerTypes,
            expenseTypes: expenseTypes, 
            expenseSubTypes: expenseSubTypes,
            expenseFrequencies: expenseFrequencies,
            incomeOwnerType: incomeOwnerType,
            incomeType: incomeType,
            incomeSubType: incomeSubType,
            incomeFrequency: incomeFrequency,
            insuranceType: insuranceType,
            insuranceSubType: insuranceSubType,
            insuranceInstallmentFrequency: insuranceInstallmentFrequency,
            goalOwnerType: ownerTypes,
            goalType: goalTypes,
            goalSubType: goalSubTypes,    
            
        };

        Object.entries(newOptionsCache).forEach(([group, values]) => {
          values.forEach(item => {
            newLookupMap.set(`${group}-${item.value}`, item.label);
          });
        });

        setLookupMap(newLookupMap);
        setOptionsCache(newOptionsCache);
        setCountries(countryData.countries);
        setCurrencies(countryData.currencies);

      } catch (error) {
        console.error("Failed to initialize lookup data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllLookups();
  }, []); // Empty dependency array ensures this runs only once

  // --- No changes to the functions below ---
  const getLabel = (group: string, value: string): string => {
    if (!value || !group) return 'N/A';
    return lookupMap.get(`${group}-${value}`) || value;
  };

  const getOptions = (group: string): LookupValue[] => {
    return optionsCache[group] || [];
  };

  const getStates = useCallback(async (countryCode: string): Promise<LookupValue[]> => {
    const cacheKey = `states-${countryCode}`;
    if (locationCache[cacheKey]) return locationCache[cacheKey];
    const states = await fetchStates(countryCode);
    setLocationCache(prev => ({ ...prev, [cacheKey]: states }));
    setLookupMap(prevMap => {
        const newMap = new Map(prevMap);
        states.forEach(state => newMap.set(`states-${state.value}`, state.label));
        return newMap;
    });
    return states;
  }, [locationCache]);

  const getCities = useCallback(async (countryCode: string, stateCode: string): Promise<LookupValue[]> => {
    const cacheKey = `cities-${countryCode}-${stateCode}`;
    if (locationCache[cacheKey]) return locationCache[cacheKey];
    const cities = await fetchCities(countryCode, stateCode);
    setLocationCache(prev => ({ ...prev, [cacheKey]: cities }));
    setLookupMap(prevMap => {
        const newMap = new Map(prevMap);
        cities.forEach(city => newMap.set(`cities-${city.value}`, city.label));
        return newMap;
    });
    return cities;
  }, [locationCache]);

  const value = { getLabel, getOptions, getStates, getCities, countries, currencies, isLoading };

  return (
    <LookupContext.Provider value={value}>
      {children}
    </LookupContext.Provider>
  );
};

// --- CUSTOM HOOK (No Changes) ---
export const useLookups = (): ILookupContext => {
  const context = useContext(LookupContext);
  if (!context) {
    throw new Error('useLookups must be used within a LookupProvider');
  }
  return context;
};